/**
 * Related-entity review, inside the triage Identification step.
 *
 * Identification surfaces entities connected to each candidate — officers of a
 * screened company, companies an individual directs. Once the analyst picks the
 * right candidate, the losing candidates' entities are superseded and the
 * winner's appear here.
 *
 * Researching one runs the deep-research graph against it and folds the result
 * into the *primary* subject's context, enriching it before screening starts.
 * It never becomes a screened subject of its own, so none of these decisions
 * change report scope or price.
 *
 * Non-blocking: the analyst can advance whenever they like, and anything left
 * is kept as context.
 */

import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';

import { useRelatedEntities } from '@/hooks/useRelatedEntities';
import {
  RELATIONSHIP_KIND_LABELS,
  type RelatedEntity,
  type RelatedEntityAction,
} from '@/types/related-entity';

interface RelatedEntitiesPanelProps {
  reportRequestId: string | null;
  /** Fired after any decision, so the stepper can refresh its gates —
   *  starting research blocks Start until it lands. */
  onChanged?: () => void;
}

const CONFIDENCE_COLOUR: Record<string, string> = {
  high: 'green',
  medium: 'yellow',
  low: 'gray',
};

function sourceUrls(entity: RelatedEntity): string[] {
  return entity.sources
    .map((s) => (typeof s.url === 'string' ? s.url : null))
    .filter((u): u is string => Boolean(u));
}

function EntityRow({
  entity,
  busy,
  onDecide,
  onSurface,
}: {
  entity: RelatedEntity;
  busy: boolean;
  onDecide?: (action: RelatedEntityAction) => void;
  onSurface?: () => void;
}) {
  const decided = entity.disposition !== 'pending';
  const urls = sourceUrls(entity);

  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Text fw={600}>{entity.name}</Text>
              {entity.entityType ? (
                <Badge variant="light" size="sm">
                  {entity.entityType}
                </Badge>
              ) : null}
              <Badge variant="outline" size="sm">
                {RELATIONSHIP_KIND_LABELS[entity.relationshipKind]}
              </Badge>
              <Badge variant="dot" size="sm" color={CONFIDENCE_COLOUR[entity.confidence] ?? 'gray'}>
                {entity.confidence}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {entity.relationship}
              {entity.relationshipDetail ? ` — ${entity.relationshipDetail}` : ''}
            </Text>
          </Stack>

          {decided ? (
            <Badge variant="light" color={entity.disposition === 'dismissed' ? 'gray' : 'blue'}>
              {entity.disposition.replace('_', ' ')}
            </Badge>
          ) : null}
        </Group>

        {entity.researchSummary ? <Text size="sm">{entity.researchSummary}</Text> : null}

        {urls.length > 0 ? (
          <Group gap="xs">
            {urls.slice(0, 3).map((url) => (
              <Anchor key={url} href={url} target="_blank" size="xs" c="dimmed">
                {new URL(url).hostname}
              </Anchor>
            ))}
          </Group>
        ) : null}

        {onDecide && !decided ? (
          <Group gap="xs">
            <Tooltip label="Run deep research on this entity and fold what it finds into the subject's context">
              <Button size="xs" loading={busy} onClick={() => onDecide('research')}>
                Research it
              </Button>
            </Tooltip>
            <Button
              size="xs"
              variant="default"
              disabled={busy}
              onClick={() => onDecide('context_only')}
            >
              Use as context
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              disabled={busy}
              onClick={() => onDecide('dismiss')}
            >
              Dismiss
            </Button>
          </Group>
        ) : null}

        {onSurface ? (
          <Group>
            <Button size="xs" variant="light" loading={busy} onClick={onSurface}>
              Add to review
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Card>
  );
}

export function RelatedEntitiesPanel({ reportRequestId, onChanged }: RelatedEntitiesPanelProps) {
  const { data, isLoading, error, decidingId, decide, surface } =
    useRelatedEntities(reportRequestId);

  if (isLoading && data.groups.length === 0) {
    return (
      <Center p="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Couldn't load related entities">
        {error.message}
      </Alert>
    );
  }

  if (data.groups.length === 0) {
    return (
      <Text c="dimmed" size="sm" p="md">
        No related entities were found for this report.
      </Text>
    );
  }

  const handleDecide = async (id: string, action: RelatedEntityAction) => {
    await decide(id, action);
    onChanged?.();
  };

  const handleSurface = async (id: string) => {
    await surface(id);
    onChanged?.();
  };

  return (
    <Stack gap="md" p="md">
      {data.pendingCount > 0 ? (
        <Alert color="blue" title="Related entities found">
          Identification found entities connected to the selected subject. Researching one enriches
          the subject before screening starts — it does <strong>not</strong> add it to the report as
          a screened subject, and does not change scope or price. Anything you leave is kept as
          context.
        </Alert>
      ) : null}

      {data.researchInFlight ? (
        <Alert color="gray" icon={<Loader size="xs" />}>
          Researching — you can keep working, but Start is held until this finishes so the results
          aren&apos;t missed.
        </Alert>
      ) : null}

      {data.groups.map((group) => (
        <Stack key={group.subjectId} gap="xs">
          <Text fw={600} size="sm">
            From {group.subjectName}
          </Text>

          {group.entities.map((entity) => (
            <EntityRow
              key={entity.id}
              entity={entity}
              busy={decidingId === entity.id}
              onDecide={(action) => void handleDecide(entity.id, action)}
            />
          ))}

          {group.undisclosedDeeper.length > 0 ? (
            <Accordion variant="contained">
              <Accordion.Item value="deeper">
                <Accordion.Control>
                  <Text size="sm">
                    {group.undisclosedDeeper.length} more found while researching
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                      One step further out. These aren&apos;t holding the report up — add one to
                      review only if it looks relevant.
                    </Text>
                    {group.undisclosedDeeper.map((entity) => (
                      <EntityRow
                        key={entity.id}
                        entity={entity}
                        busy={decidingId === entity.id}
                        onSurface={() => void handleSurface(entity.id)}
                      />
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}
